import React, { useEffect, useState, useRef, useCallback } from 'react'
import { fynitoApi } from '../Services/axios';
import { fetchPetpoojaMenu } from '../../redux/features/petpooja/petpoojaSlice';
import { transformPetpoojaCategories } from '../../utils/petpoojaTransformers';
import { useDispatch, useSelector } from 'react-redux';
import toastify from '../../helper/toastify';
import apiCache from '../../services/apiCache';

const MenuItem = ({ restaurantId, filter, setFilter, fatchapi, isPetpoojaIntegrated = false, petpoojaID = null }) => {
    const dispatch = useDispatch();
    const [menuList, setMenuList] = useState([]);
    const [loading, setLoading] = useState(false);
    const { petpoojaMenus } = useSelector(state => state.petpooja);
    const fetchingRef = useRef(false);
    const lastRestaurantIdRef = useRef(null);

    // Fetch Menu Item Count Api - memoized to prevent unnecessary re-creation
    const fetchMenuItems = useCallback(async () => {
        // Prevent duplicate calls
        if (fetchingRef.current || !restaurantId) {
            return;
        }

        // Skip if same restaurant was just fetched
        if (lastRestaurantIdRef.current === restaurantId && !isPetpoojaIntegrated) {
            return;
        }

        fetchingRef.current = true;
        lastRestaurantIdRef.current = restaurantId;
        
        try {
            if (isPetpoojaIntegrated && petpoojaID) {
                // Use Petpooja categories if available
                if (petpoojaMenus[petpoojaID]) {
                    const categories = transformPetpoojaCategories(petpoojaMenus[petpoojaID]);
                    setMenuList(categories);
                    fetchingRef.current = false;
                    return;
                } else {
                    // Fetch Petpooja menu if not already loaded
                    await dispatch(fetchPetpoojaMenu(petpoojaID));
                    fetchingRef.current = false;
                    return;
                }
            } else {
                // Check cache first (auth-free web endpoint)
                const cacheKey = apiCache.getCacheKey('/category-with-menu-count-for-web', 'POST', {}, { restaurant_id: restaurantId });
                const cachedData = apiCache.get(cacheKey, 300000); // 5 minutes cache
                
                if (cachedData) {
                    setMenuList(cachedData);
                    fetchingRef.current = false;
                    return;
                }
                
                // Check if request is already pending
                if (apiCache.isPending(cacheKey)) {
                    const pendingResponse = await apiCache.getPending(cacheKey);
                    if (pendingResponse?.data?.status_code === 200) {
                        setMenuList(pendingResponse.data.data);
                        apiCache.set(cacheKey, pendingResponse.data.data);
                    }
                    fetchingRef.current = false;
                    return;
                }
                
                // Use regular API
                const requestPromise = fynitoApi.post('/category-with-menu-count-for-web', { restaurant_id: restaurantId });
                apiCache.setPending(cacheKey, requestPromise);
                const res = await requestPromise;
                
                if (res.data.status_code === 200) {
                    setMenuList(res.data.data);
                    apiCache.set(cacheKey, res.data.data);
                } else {
                    toastify.error(res.data.message);
                }
            }
        } catch (error) {
            console.error("Error fetching menu items:", error);
        } finally {
            fetchingRef.current = false;
        }
    }, [restaurantId, isPetpoojaIntegrated, petpoojaID, petpoojaMenus, dispatch]);

    // Consolidated: Single useEffect to fetch menu items when restaurantId or integration status changes
    useEffect(() => {
        if (restaurantId) {
            fetchMenuItems();
        }
    }, [restaurantId, fetchMenuItems]);

    // Update categories when Petpooja data is loaded (separate effect for Petpooja data updates)
    useEffect(() => {
        if (isPetpoojaIntegrated && petpoojaID && petpoojaMenus[petpoojaID]) {
            const categories = transformPetpoojaCategories(petpoojaMenus[petpoojaID]);
            setMenuList(categories);
        }
    }, [petpoojaMenus, petpoojaID, isPetpoojaIntegrated]);

    // Handle category selection
    const handleCategorySelect = useCallback((categoryId) => {
        if (loading) return;
        setLoading(true);
        setFilter(prevFilter => ({
            ...prevFilter,
            menu_category_id: categoryId
        }));
        // Close the modal
        const modal = document.getElementById('menu-item');
        const bsModal = window.bootstrap.Modal.getInstance(modal);
        if (bsModal) bsModal.hide();
        setTimeout(() => setLoading(false), 500);
    }, [loading, setFilter]);

    // Handle "All" selection
    const handleAllSelect = useCallback(() => {
        if (loading) return;
        setLoading(true);
        setFilter(prevFilter => ({
            ...prevFilter,
            menu_category_id: ""
        }));
        // Close the modal
        const modal = document.getElementById('menu-item');
        const bsModal = window.bootstrap.Modal.getInstance(modal);
        if (bsModal) bsModal.hide();

        // Wait for state to update before fetching
        setTimeout(() => setLoading(false), 500);
    }, [loading, setFilter]);

    // Call fatchapi when menu_category_id changes (this triggers restaurant menu refresh)
    useEffect(() => {
        if (restaurantId && filter?.menu_category_id !== undefined) {
            fatchapi();
        }
    }, [filter?.menu_category_id, restaurantId, fatchapi]);
    
    return (
        <>
            <div className="modal fade menu-item-modal" id="menu-item" tabIndex={-1} aria-labelledby="exampleModalLabel" aria-hidden="true" >
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h3 className="modal-title" id="exampleModalLabel"> Menu Items </h3>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"><i className='fas fa-times'></i></button>
                        </div>
                        <div className="modal-body">
                            <ul className='menu-item-modal-list'>
                                <li className={filter?.menu_category_id === "" ? "active" : ""}
                                    onClick={handleAllSelect}>
                                    <span>All</span>
                                    <span>1</span>
                                </li>
                                {
                                    menuList?.map((menu, index) => {
                                        return (
                                            <li key={menu.id}
                                                className={filter?.menu_category_id === menu.id ? "active" : ""}
                                                onClick={() => handleCategorySelect(menu.id)}>
                                                <span>{menu.title}</span>
                                                <span>{menu.total_item}</span>
                                            </li>
                                        )
                                    })
                                }
                            </ul>
                        </div>

                    </div>
                </div>
            </div>
        </>
    )
}

export default MenuItem